import { ENVIRONMENT_HELPER } from "@/helpers/environment.js";
import store from '@/store.js'
import i18n from '@/helpers/i18n';
import { apiErrorField } from '@/helpers/utils.js';

export const IS_LOADING_STATUS = "loading";

export const API_HELPER = {
    abortController: new AbortController(),
    pathJoin,
    loggedApiCall,
    apiCall,
    apiError
};

// The API's {Errors: {key: message}} → [{key: field, value}] for the forms (errorValidator / errorOutput /
// FormApiErrors); some views read them by the API's own key too. One entry per field, its messages joined.
// `body` is the request's JSON: a list item's index (Values[1].Value) is the position of the language in the
// dictionary as sent, so the message says which language it is about. Anything else becomes a generic message.
const BODY_UNREADABLE = '_request';
function apiError(err, body)
{
    if(err && err.Errors)
    {
        let sent = null;
        try { sent = typeof body === 'string' ? JSON.parse(body) : body; } catch (e) { sent = null; }

        let errorArray = [];
        for (var i = 0; i < Object.keys(err.Errors).length; i++) {
            let key = Object.keys(err.Errors)[i];
            let value = Array.isArray(err.Errors[key]) ? err.Errors[key].join(' ') : err.Errors[key];
            let field = apiErrorField(key);
            // the model binder (body not readable: bad JSON, wrong type, unknown enum) keys errors by JSON path
            // in camelCase; none of them is a form field, the envelope's Message says what happened
            if (/^[a-z]/.test(key)) {
                field = BODY_UNREADABLE;
                value = err.Message || value;
            }
            else {
                let item = key.match(/^(\w+)\[(\d+)\]\.Value$/);
                let dictionary = item && sent ? sent[Object.keys(sent).find(k => k.toLowerCase() === item[1].toLowerCase())] : null;
                if (dictionary && typeof dictionary === 'object' && !Array.isArray(dictionary) && Object.keys(dictionary)[item[2]]) {
                    value = `${Object.keys(dictionary)[item[2]].toUpperCase()}: ${value}`;
                }
                else if (field !== key && /[[.]/.test(key)) {
                    value = `${key}: ${value}`; // a nested path the request did not carry (e.g. validated on the stored entity)
                }
            }
            let entry = errorArray.find(e => e.key === field);
            if (!entry) errorArray.push({ key: field, value: value });
            else if (!entry.value.includes(value)) entry.value += ' ' + value;
            errorArray[key] = value;
            if (!(field in errorArray)) errorArray[field] = value;
        }

        return errorArray;
    }

    return "Something went wrong, please try again. If the problem persists contact the support.";
}

function pathJoin(parts)
{
    return parts.map((part, i) => 
    {
        if (i === 0) {
          return part.toString().trim().replace(/[\/]*$/g, '')
        } else {
          return part.toString().trim().replace(/(^[\/]*|[\/]*$)/g, '')
        }
    }).filter(x=>x.length).join('/');
}

/* Handles 1) request cancellation 2) refresh the token if the user is not logged in 3) fetch url 4) error handler */
function loggedApiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Validate if the user is logged in.
    let user = JSON.parse(localStorage.getItem('user'));
    if(!user || !user.data.accessToken)
    {
        throw("The user is not logged in or the current session is not valid!");
    }

    // Check if token is expired. Return authorization header with jwt token and compare expired time with the next 10 minutes. The 10 minutes is just a buffer that we allow ourselves to refresh the token. 
    var expiredTime = new Date(user.expired_time);

    var limitTime = new Date();
    limitTime.setMinutes(limitTime.getMinutes() + 10);
    var limitTimeUtc = new Date(limitTime.getUTCFullYear(), limitTime.getUTCMonth(), limitTime.getUTCDate(), limitTime.getUTCHours(), limitTime.getUTCMinutes(), limitTime.getUTCSeconds());

    if(expiredTime < limitTimeUtc)
    {
        // TODO Here we will refresh the token
    }

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }


    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    // Add current language to request
    if(!requestOptions.headers) 
    {
        requestOptions.headers = {};
    }
    requestOptions.headers["Accept-Language"] = i18n.locale;

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            // Saved, but some e-mails could not be sent: a partial success, not a failure (the data is there;
            // retrying would duplicate it). The caller proceeds as on success and the user sees a warning.
            if (response.status === 502 && data && data.Errors && data.Errors.EmailNotSent) {
                store.dispatch('alert/warning', i18n.t('emailNotSentWarning', { recipients: data.Errors.EmailNotSent }), { root: true });
                return data;
            }
            if (!response.ok) {
                var errorMsg;
                switch(response.status)
                {
                    case 401:
                        {
                            errorMsg = "The token is not valid.";

                            store.dispatch('users/logout', null, { root: true });
                            break;
                        }
                    default:
                        {
                            errorMsg = data;
                            break;
                        }
                }

                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err && err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            return Promise.reject(apiError(err, requestOptions.body));
        }
    });
}

/* Handles 1) request cancellation 2) fetch url 3) error handler */
function apiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }

    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                // was a reference to an undefined `error`, so every anonymous call (login, confirm account,
                // reset password) failed with a generic message instead of the API's errors
                return Promise.reject(data);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err && err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            return Promise.reject(apiError(err, requestOptions.body));
        }
    });
}